import { TextInput } from '../../components/inputs/text-input';
import { RadioGroup } from '../../components/inputs/radio-group';
import styles from './patient-demographics.module.css';
import { Checkbox } from '@/components/inputs/checkbox';
import { z } from 'zod';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from '@/components/form';
import {
  optionalAddress,
  requiredAddress,
  requiredPastDate,
  requiredPhoneNumber,
  requiredState,
  requiredString,
  requiredZipCode,
  zt,
} from '@/util/validators';
import { useTranslation } from '@/contexts/i18n-context';
import { useNavigate } from '@/util/react-router-dom';
import { useLoggedUser } from '@/contexts/current-user-context';
import { StateSelector } from '@/components/form-controls/state-selector';
import { PhoneInput } from '@/components/inputs/phone-input';
import { PreferredTitleSelector } from '@/components/form-controls/preferred-title-selector';
import clsx from 'clsx';
import { Gender, GetPersonalInfoResponse, PreferredTitle } from '@/api/api.schemas';
import { useMediaQuery } from '@/hooks/use-media-query';
import { GradientFormTitle } from '@/components/containers/gradient-form-title';
import { Section } from '@/components/containers/section';
import { ProfileCreationNavigation } from '@/components/navigation/profile-creation-navigation';
import { useProfileCreation } from '@/contexts/profile-creation-context';
import { NavButton } from '@/components/navigation/nav-button';
import { getGetPatientQueryKey } from '@/api/get-personal-info';
import { useQueryClient } from '@tanstack/react-query';
import { PageContainer } from '@/components/containers/page-container';
import { FormButtonRow } from '@/components/containers/form-button-row';
import { formatPhoneNumber, getAge } from '@/util/helpers';
import { useUpdatePatientInfo } from '@/api/update-personal-info';
import { useBranding } from '@/outlets/branding-outlet';
import { getPatientCreationConfig, getPatientEditConfig, Mode } from './demographics-config';
import { Banner } from '@/components/banner';
import { useState } from 'react';
import type { FocusEvent } from 'react';
import { IdentityVerificationResetModal } from '@/components/identity-verification/identity-verification-reset-modal';
import { hasIdentityFieldsChanged } from '@/util/identity-fields';

const schema = z
  .object({
    firstName: requiredString(),
    lastName: requiredString(),
    preferredTitle: z.nativeEnum(PreferredTitle, {
      errorMap: () => ({ message: zt('common.errors.required') }),
    }),
    mailingAddress1: requiredAddress(),
    mailingAddress2: optionalAddress(),
    city: requiredString(),
    state: requiredState(),
    zip: requiredZipCode(),
    email: requiredString().email(zt('common.errors.email')),
    cellPhone: requiredPhoneNumber(zt('patientProfile.phone.error')),
    gender: z.nativeEnum(Gender, { errorMap: () => ({ message: zt('common.errors.required') }) }),
    genderOther: z.string().optional(),
    dateOfBirth: requiredPastDate(),
    textMe: z.boolean().optional(),
  })
  .refine((data) => data.gender !== Gender.other || data.genderOther?.trim(), {
    message: zt('common.errors.required'),
    path: ['genderOther'],
  });

type FormValues = z.infer<typeof schema>;

interface PatientDemographicsProps {
  mode: Mode;
}

export function PatientDemographics({ mode }: PatientDemographicsProps) {
  const { patient } = useProfileCreation();

  if (!patient) {
    return null;
  }

  return <PatientDemographicsForm patient={patient} mode={mode} />;
}

interface PatientDemographicsFormProps extends PatientDemographicsProps {
  patient: GetPersonalInfoResponse;
}

function PatientDemographicsForm({ patient, mode }: PatientDemographicsFormProps) {
  const { t } = useTranslation();
  const { readonlyProfile, hasIdVerification } = useBranding();
  const { withSurrogate, setAboveEighteen, aboveEighteen } = useProfileCreation();
  const queryClient = useQueryClient();
  const [pendingFormData, setPendingFormData] = useState<FormValues>();

  const config =
    mode === 'create' ? getPatientCreationConfig(t, withSurrogate) : getPatientEditConfig(t);

  const defaultValues = {
    ...patient,
    gender: patient.gender ?? undefined,
    genderOther: patient.genderOther ?? '',
    mailingAddress1: patient.mailingAddress1 ?? '',
    mailingAddress2: patient.mailingAddress2 ?? '',
    city: patient.city ?? '',
    state: patient.state ?? '',
    zip: patient.zip ?? '',
    dateOfBirth: patient.dateOfBirth ?? '',
    cellPhone: formatPhoneNumber(patient.cellPhone),
    textMe: withSurrogate ? undefined : (patient.textMe ?? false),
  };

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    criteriaMode: 'firstError',
    defaultValues,
  });

  const { mutateAsync: savePersonalInfo, isPending } = useUpdatePatientInfo();
  const navigate = useNavigate();
  const { user } = useLoggedUser();

  const showIdentityVerificationWarnings = hasIdVerification && patient.idvVerified;

  const saveAndNavigate = async (formData: FormValues) => {
    await savePersonalInfo(
      { patientId: user!.id, data: formData },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getGetPatientQueryKey(user.id),
            refetchType: 'all',
          });
        },
      },
    );

    navigate(config.nextRoute);
  };

  const onSubmit: SubmitHandler<FormValues> = async (formData) => {
    if (
      config.validateAge &&
      aboveEighteen &&
      !withSurrogate &&
      !isAgeAboveEighteenOrNull(formData.dateOfBirth)
    ) {
      form.setError(
        'dateOfBirth',
        {
          message: t('patientProfile.dateOfBirth.minorError'),
        },
        { shouldFocus: true },
      );
      return;
    }

    if (
      showIdentityVerificationWarnings &&
      hasIdentityFieldsChanged(
        {
          ...defaultValues,
          birthday: defaultValues.dateOfBirth,
        },
        { ...formData, birthday: formData.dateOfBirth },
      )
    ) {
      setPendingFormData(formData);
      return;
    }

    await saveAndNavigate(formData);
  };

  const handleConfirmIdentityReset = async () => {
    if (!pendingFormData) {
      return;
    }

    await saveAndNavigate(pendingFormData);
    setPendingFormData(undefined);
  };

  const handleDateOfBirthBlur = (event: FocusEvent<HTMLInputElement>) => {
    const dateOfBirth = event.target.value;

    if (isAgeAboveEighteenOrNull(dateOfBirth)) {
      setAboveEighteen(true);
    }
  };

  const isFieldDisabled = <T,>(
    fieldName: keyof GetPersonalInfoResponse,
    defaultValue?: T,
  ): boolean => {
    if (!readonlyProfile) return false;

    const value = patient[fieldName];

    if (value == null) return false;

    if (defaultValue !== undefined && value === defaultValue) {
      return false;
    }

    if (typeof value === 'string') {
      return value.trim().length > 0;
    }

    return !!value;
  };

  const textMe = form.watch('textMe');
  const genderValue = form.watch('gender');

  const isMobile = useMediaQuery('--screen-md');

  return (
    <PageContainer>
      {!isMobile && (
        <NavButton text={config.navigationButton.textKey} to={config.navigationButton.route} />
      )}
      <GradientFormTitle>
        {isMobile && (
          <NavButton text={config.navigationButton.textKey} to={config.navigationButton.route} />
        )}
        {config.showStepper && <ProfileCreationNavigation />}
        <h1 className={clsx('h3', styles.pageTitle)}>{config.title}</h1>
        <p>
          {t(`patientProfile.${withSurrogate ? 'pageDescriptionSurrogate' : 'pageDescription'}`)}
        </p>
      </GradientFormTitle>

      <Form form={form} onSubmit={onSubmit}>
        <Section>
          {readonlyProfile && (
            <Banner type="info" className={styles.readOnlyBanner}>
              <span className="semibold">{t('patientProfile.readOnlyBanner')}</span>
            </Banner>
          )}
          <h2 className="h4">
            {t(`patientProfile.personalInfo.${withSurrogate ? 'titleSurrogate' : 'title'}`)}
          </h2>
          <p className={styles.sectionDescription}>
            {t(
              `patientProfile.personalInfo.${withSurrogate ? 'descriptionSurrogate' : 'description'}`,
            )}
          </p>
          <div className={styles.formInputs}>
            <TextInput
              name="firstName"
              label={t(
                `patientProfile.patientName.firstName.${withSurrogate ? 'labelSurrogate' : 'label'}`,
              )}
              placeholder={t(
                `patientProfile.patientName.firstName.${withSurrogate ? 'placeholderSurrogate' : 'placeholder'}`,
              )}
              disabled={isFieldDisabled('firstName')}
              warningOnChange={
                showIdentityVerificationWarnings
                  ? {
                      message: t('identityVerificationWarnings.field.name'),
                      defaultValue: defaultValues.firstName,
                    }
                  : undefined
              }
            />
            <TextInput
              name="lastName"
              label={t(
                `patientProfile.patientName.lastName.${withSurrogate ? 'labelSurrogate' : 'label'}`,
              )}
              placeholder={t(
                `patientProfile.patientName.lastName.${withSurrogate ? 'placeholderSurrogate' : 'placeholder'}`,
              )}
              disabled={isFieldDisabled('lastName')}
              warningOnChange={
                showIdentityVerificationWarnings
                  ? {
                      message: t('identityVerificationWarnings.field.name'),
                      defaultValue: defaultValues.lastName,
                    }
                  : undefined
              }
            />
            <PreferredTitleSelector
              name="preferredTitle"
              label={t(
                `patientProfile.titleSelector.${withSurrogate ? 'labelSurrogate' : 'label'}`,
              )}
              placeholder={t(
                `patientProfile.titleSelector.${withSurrogate ? 'placeholderSurrogate' : 'placeholder'}`,
              )}
              disabled={isFieldDisabled<PreferredTitle>('preferredTitle', PreferredTitle.none)}
            />
            <TextInput
              name="dateOfBirth"
              label={t(`patientProfile.dateOfBirth.${withSurrogate ? 'labelSurrogate' : 'label'}`)}
              type="date"
              description={t('patientProfile.dateOfBirth.description')}
              withIconsIndicators={false}
              disabled={isFieldDisabled('dateOfBirth')}
              warningOnChange={
                showIdentityVerificationWarnings
                  ? {
                      message: t('identityVerificationWarnings.field.dateOfBirth'),
                      defaultValue: defaultValues.dateOfBirth,
                    }
                  : undefined
              }
              onBlur={handleDateOfBirthBlur}
            />

            <RadioGroup
              name="gender"
              className={styles.genderToggle}
              fieldSetClassName={styles.fullRow}
              variant="selector"
              label={t('patientProfile.gender.label')}
              subLabel={t('patientProfile.gender.description')}
              options={Object.values(Gender).map((gender) => ({
                value: gender,
                label: t(`common.gender.${gender}`),
              }))}
              disabled={isFieldDisabled('gender')}
            />
          </div>

          {genderValue === Gender.other && (
            <TextInput
              aria-label={t(
                `patientProfile.gender.${withSurrogate ? 'genderOtherSurrogate' : 'genderOther'}`,
              )}
              className={styles.genderOther}
              name="genderOther"
              placeholder={t(
                `patientProfile.gender.${withSurrogate ? 'genderOtherSurrogate' : 'genderOther'}`,
              )}
              disabled={isFieldDisabled('genderOther')}
            />
          )}
        </Section>
        <Section>
          <h2 className="h4">
            {t(`patientProfile.contactInfo.${withSurrogate ? 'titleSurrogate' : 'title'}`)}
          </h2>
          <p className={styles.sectionDescription}>
            {t(
              `patientProfile.contactInfo.${withSurrogate ? 'descriptionSurrogate' : 'description'}`,
            )}
          </p>
          <div className={styles.formInputs}>
            <TextInput
              name="email"
              disabled
              label={t('patientProfile.email.label')}
              placeholder={t('patientProfile.email.placeholder')}
            />

            <PhoneInput
              name="cellPhone"
              label={t('patientProfile.phone.label')}
              disabled={isFieldDisabled('cellPhone')}
            />

            <TextInput
              name="mailingAddress1"
              className={styles.fullRow}
              label={t('patientProfile.address.address1.label')}
              placeholder={t('patientProfile.address.address1.placeholder')}
              description={t('patientProfile.address.address1.description')}
              disabled={isFieldDisabled('mailingAddress1')}
            />
            <TextInput
              name="mailingAddress2"
              className={styles.fullRow}
              label={t('patientProfile.address.address2.label')}
              placeholder={t('patientProfile.address.address2.placeholder')}
              description={t('patientProfile.address.address2.description')}
              disabled={isFieldDisabled('mailingAddress2')}
            />
            <TextInput
              name="city"
              label={t('patientProfile.address.city.label')}
              placeholder={t('patientProfile.address.city.placeholder')}
              disabled={isFieldDisabled('city')}
            />
            <StateSelector
              name="state"
              placeholder={t('patientProfile.address.state.placeholder')}
              label={t('patientProfile.address.state.label')}
              disabled={isFieldDisabled('state')}
            />
            <TextInput
              name="zip"
              label={t('patientProfile.address.zip.label')}
              placeholder={t('patientProfile.address.zip.placeholder')}
              disabled={isFieldDisabled('zip')}
            />
          </div>
        </Section>
        {config.showTextMeSection && (
          <div className={styles.textMeSection}>
            <h1 className={styles.title}>{t('patientProfile.textMe.title')}</h1>
            <Checkbox label={t('patientProfile.textMe.label')} name="textMe" />
            {textMe && <i>{t('patientProfile.textMe.description')}</i>}
          </div>
        )}

        <FormButtonRow
          nextStep={config.nextStep}
          buttonTextKey={config.submitButton.textKey}
          type="submit"
          rightIcon={config.submitButton.icon}
          loading={isPending}
          secondaryButton={config.secondaryButton}
        />
      </Form>
      <IdentityVerificationResetModal
        isOpen={!!pendingFormData}
        onClose={() => setPendingFormData(undefined)}
        onConfirm={handleConfirmIdentityReset}
        isLoading={isPending}
      />
    </PageContainer>
  );
}

function isAgeAboveEighteenOrNull(date: string) {
  const age = getAge(date);

  if (age === null) {
    return null;
  }

  return age >= 18;
}
